import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../api";

export default function ProductCommissionRules({ affiliateId, disabled = false }) {
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [rate, setRate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [revision, setRevision] = useState(0);
  const rateInputRef = useRef(null);
  const limit = 50;

  useEffect(() => {
    let current = true;
    setLoading(true);
    setEditing(null);
    const timer = setTimeout(async () => {
      try {
        const response = await api.get(`/affiliates/${affiliateId}/product-commissions`, { params: { search, offset, limit } });
        if (current) { setPage(response.data); setError(""); }
      } catch (err) {
        if (current) setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        if (current) setLoading(false);
      }
    }, 200);
    return () => { current = false; clearTimeout(timer); };
  }, [affiliateId, search, offset, revision]);

  useEffect(() => {
    if (editing && rateInputRef.current) {
      rateInputRef.current.focus();
      rateInputRef.current.select();
    }
  }, [editing]);

  const closeEditor = () => {
    if (saving) return;
    setEditing(null);
    setError("");
  };

  const save = async (event) => {
    event.preventDefault();
    const value = Number(rate);
    if (!rate.trim() || !Number.isFinite(value) || value < 0 || value > 100) {
      setError("Commission must be between 0 and 100.");
      return;
    }
    setSaving(true);
    setError("");
    setSaved("");
    try {
      await api.put(`/affiliates/${affiliateId}/product-commissions/${editing.productId}`, { rate: value });
      setSaved(`${editing.title}: commission saved. Future orders will use the new rate.`);
      setEditing(null);
      setRevision((value) => value + 1);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return <section aria-label="Product commission rules">
    <h2 className="section-title">Product commission rules</h2>
    <p className="muted">Products not configured for commission receive 0%.</p>
    <label htmlFor="product-commission-search">Search product</label>
    <input id="product-commission-search" value={search} disabled={saving}
      onChange={(event) => { setSearch(event.target.value); setOffset(0); setSaved(""); }} placeholder="Search product name" />
    {error && !editing && <div className="alert" role="alert">{error}</div>}
    {saved && <p role="status">{saved}</p>}
    {loading ? <p className="loading">Loading products…</p> : <>
      <div className="table-wrap"><table><thead><tr><th>Product name</th><th>Current percentage</th><th>Action</th></tr></thead>
        <tbody>{page.items.map((product) => <tr key={product.productId}>
          <td>{product.title}</td><td>{product.rate}%</td>
          <td><button type="button" className="btn btn-ghost btn-sm" disabled={disabled || saving} onClick={() => {
            setEditing(product); setRate(String(product.rate)); setError(""); setSaved("");
          }}>Edit</button></td>
        </tr>)}</tbody></table></div>
      {!page.items.length && <p className="empty">No synchronized products found.</p>}
      <div className="actions" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-ghost" disabled={offset === 0 || saving} onClick={() => setOffset(Math.max(0, offset - limit))}>Previous</button>
        <span>{page.total ? `${offset + 1}–${Math.min(offset + limit, page.total)} of ${page.total}` : "0 products"}</span>
        <button type="button" className="btn btn-ghost" disabled={offset + limit >= page.total || saving} onClick={() => setOffset(offset + limit)}>Next</button>
      </div>
    </>}

    {editing && !disabled && <div className="modal-overlay" onClick={closeEditor}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="product-commission-editor-title"
        onClick={(event) => event.stopPropagation()} data-testid="product-commission-editor">
        <h2 id="product-commission-editor-title">Edit product commission</h2>
        <p className="muted">{editing.title}</p>
        {error && <div className="alert" role="alert">{error}</div>}
        <form onSubmit={save}>
          <label htmlFor="product-commission-rate">Commission %</label>
          <input ref={rateInputRef} id="product-commission-rate" type="number" min="0" max="100" step="0.01" required value={rate}
            disabled={saving} onChange={(event) => setRate(event.target.value)} />
          <p className="muted small">The new percentage applies to future orders only. Historical commissions remain unchanged.</p>
          <div className="modal-actions">
            <button className="btn btn-ghost" type="button" disabled={saving} onClick={closeEditor}>Cancel</button>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Save commission"}</button>
          </div>
        </form>
      </div>
    </div>}
  </section>;
}
